import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoClose } from "react-icons/io5";
import { DecorativeElement } from "../../components/Decorative";

const images = [
  {
    src: "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=800&h=600&fit=crop",
    title: "Signature Curries",
    category: "food",
  },
  {
    src: "https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?w=800&h=600&fit=crop",
    title: "Aromatic Biryanis",
    category: "food",
  },
  {
    src: "https://images.unsplash.com/photo-1585937421612-34e1df62c4d9?w=800&h=600&fit=crop",
    title: "Professional Kitchen",
    category: "kitchen",
  },
  {
    src: "https://images.unsplash.com/photo-1567188040759-fb8a883dc6d8?w=800&h=600&fit=crop",
    title: "Tandoor Specials",
    category: "food",
  },
  {
    src: "https://images.unsplash.com/photo-1601050690597-df0568f70950?w=800&h=600&fit=crop",
    title: "Traditional Thalis",
    category: "food",
  },
  {
    src: "https://images.unsplash.com/photo-1526367790999-0150786686a2?w=800&h=600&fit=crop",
    title: "Fast Delivery Service",
    category: "delivery",
  },
  {
    src: "https://images.unsplash.com/photo-1574484284002-952d92456975?w=800&h=600&fit=crop",
    title: "Delicious Appetizers",
    category: "food",
  },
  {
    src: "https://images.unsplash.com/photo-1606787620819-8bdf0c44c293?w=800&h=600&fit=crop",
    title: "Fresh Ingredients",
    category: "kitchen",
  },
  {
    src: "https://images.unsplash.com/photo-1596797038530-2c107229654b?w=800&h=600&fit=crop",
    title: "Fresh Spices",
    category: "food",
  },
  {
    src: "https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=800&h=600&fit=crop",
    title: "Ready for Packaging",
    category: "delivery",
  },
  {
    src: "https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=800&h=600&fit=crop",
    title: "Butter Chicken",
    category: "food",
  },
  {
    src: "https://images.unsplash.com/photo-1603894584373-5ac82b2ae398?w=800&h=600&fit=crop",
    title: "Catering Service",
    category: "delivery",
  },
];

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [filter, setFilter] = useState<string>("all");

  const filteredImages =
    filter === "all" ? images : images.filter((img) => img.category === filter);

  return (
    <div className="min-h-screen w-full py-12">
      <div className="max-w-7xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-headingColor mb-4">
            Our Gallery
          </h1>
          <p className="text-textColor text-lg mb-8">
            Explore our delicious dishes, professional kitchen, and delivery
            service
          </p>
          <DecorativeElement type="divider" />
        </motion.div>

        <div className="flex justify-center gap-4 mb-12">
          {["all", "food", "kitchen", "delivery"].map((cat) => (
            <motion.button
              key={cat}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setFilter(cat)}
              className={`px-6 py-2 rounded-full capitalize transition-all duration-300 ${
                filter === cat
                  ? "bg-gradient-to-r from-[#947d25] to-[#ac9430] text-white shadow-lg"
                  : "bg-gray-100 text-textColor hover:bg-gray-200"
              }`}
            >
              {cat}
            </motion.button>
          ))}
        </div>

        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {filteredImages.map((image, index) => (
            <motion.div
              layout
              key={index}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              onClick={() => setSelectedImage(index)}
              className="group relative cursor-pointer overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="aspect-w-4 aspect-h-3"
              >
                <img
                  src={image.src}
                  alt={image.title}
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <div className="text-center px-4">
                  <h3 className="text-white text-xl font-semibold mb-2">
                    {image.title}
                  </h3>
                  <span className="text-white text-sm bg-[#ac9430] bg-opacity-90 px-3 py-1 rounded-full">
                    {image.category}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <AnimatePresence>
          {selectedImage !== null && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedImage(null)}
              className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.5 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.5 }}
                className="relative max-w-4xl w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setSelectedImage(null)}
                  className="absolute -top-10 right-0 text-white text-3xl hover:text-[#ac9430] transition-colors"
                >
                  <IoClose />
                </button>
                <img
                  src={images[selectedImage].src}
                  alt={images[selectedImage].title}
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
                <div className="text-center mt-4">
                  <h3 className="text-white text-xl font-semibold">
                    {images[selectedImage].title}
                  </h3>
                  <p className="text-gray-300 mt-2">Click anywhere to close</p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default GalleryPage;
